// useTarifas.js
import { useState, useCallback } from "react";
import { get_tarifas } from "../../services/tarifas";
import { TARIFAS_OFERTA } from "../../utils/constants/tarifas";

// Hook que carrega les tarifes comercials disponibles del formulari.
// S'utilitza quan l'usuari ja ha triat sector, tarifa d'acces i mode de facturacio.
export const useTarifas = (sector_type) => {
    const [tarifas, setTarifas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [disableTF, setDisableTF] = useState(true);

    // Activa la carrega de tarifes comercials disponibles.
    // Rep la tarifa seleccionada al formulari, el mode de facturacio i l'usuari,
    // resol la clau de tarifa i demana al backend les opcions valides.
    const enableTarifas = useCallback(async (tarifaOferta, mode_facturacio, user, typeOferta) => {
        // Normalitza la tarifa a numeric per comparar-la amb les constants locals.
        const tarifaId = typeof tarifaOferta === 'string' ? parseInt(tarifaOferta, 10) : tarifaOferta;

        // Si falta context minim, el selector de tarifes comercials ha de quedar deshabilitat.
        if (!tarifaId || !mode_facturacio || !sector_type) {
            setDisableTF(true);
            return;
        }

        setLoading(true);
        setDisableTF(true);

        try {
            // Busca la clau interna de la tarifa d'acces que el servei necessita.
            const tarifaKey = TARIFAS_OFERTA[sector_type].find(t => {
                // Forca comparacio numerica per evitar errors entre string i int.
                const tId = typeof t.id === 'string' ? parseInt(t.id, 10) : t.id;
                return tId === tarifaId;
            })?.key;

            if (!tarifaKey) {
                throw new Error(`Tarifa ${tarifaId} no encontrada`);
            }
            const res = await get_tarifas(
                sector_type,
                tarifaKey,
                mode_facturacio,
                user.first_name,
                typeOferta
            );

            // Si gas -> tarifes_atr_gas_compatibles, si luz -> tarifes_atr_compatibles
            const compatibleField = sector_type === 'gas'
                ? 'tarifes_atr_gas_compatibles'
                : 'tarifes_atr_compatibles';

            // Si gas -> compatible_invoicing_modes_gas, si luz -> compatible_invoicing_modes
            const invoicingModesField = sector_type === 'gas'
                ? 'compatible_invoicing_modes_gas'
                : 'compatible_invoicing_modes';
            
            // Si tarifa Fija -> 1, si Indexada -> 2
            const invoicingModeId = mode_facturacio === 'atr' ? 1 : 2;

            // El mode ERP es obligatori; la compatibilitat ATR pot venir del tag
            // o del codi historic conservat en el nom.
            const placeholderTarifa = Array.isArray(res)
                ? res.find((tarifa) => Number(tarifa?.id) === 0)
                : null;
            const filteredTarifasReales = Array.isArray(res) ? res.filter((tarifa) => {
                if (Number(tarifa?.id) === 0) {
                    return false;
                }
                // Comprueba si la tarifa comerical es compatible con la tarifa de acceso
                const compatibles = Array.isArray(tarifa?.[compatibleField]) // Lee cada elemento del array y devuelve el array numerico
                    ? tarifa[compatibleField].map((value) => Number(value))
                    : [];
                // export const TARIFAS_OFERTA = {
                //     electrico: [
                //         { id: 41, name: "2.0TD", key: "20" },
                //         { id: 42, name: "3.0TD", key: "30" },
                //         { id: 43, name: "6.1TD", key: "6" },
                //     ],
                //     gas: [
                //         { id: 11, name: "RL01", key: "RL01" },
                //         { id: 12, name: "RL02", key: "RL02" },
                //         { id: 13, name: "RL03", key: "RL03" },
                //         { id: 14, name: "RL04", key: "RL04" },
                //         { id: 22, name: "RLTB5", key: "RLTB5" },
                //     ]
                // };  
                // Comprueba que tarifa comercial este activa para la tarifa de acceso
                const hasCompatibleTag = compatibles.includes(Number(tarifaId)); 
                // Normalitza numericamente array compatible_invoicing
                const invoicingModes = Array.isArray(tarifa?.[invoicingModesField])
                    ? tarifa[invoicingModesField].map((value) => Number(value))
                    : [];
                const tarifaName = String(tarifa?.name || "").toUpperCase();
                let hasCompatibleInvoicingMode;
                if(invoicingModes.length > 0) {
                    // Si el ERP define explícitamente compatible_invoicing, tienen prioridad
                    hasCompatibleInvoicingMode = invoicingModes.includes(invoicingModeId);
                } 
                else {
                    // Fallback: IND al nom quan no hi ha tags definits en el ERP.
                    if(mode_facturacio === "index") {
                        hasCompatibleInvoicingMode = tarifaName.includes("IND");
                    } else {
                        hasCompatibleInvoicingMode = !tarifaName.includes("IND");
                    }
                }
                // Comprueba que el nombre de la tarifa contenga "tarifaKey"
                const hasCompatibleName = tarifaName
                    .split(/[\s\-._]+/) // Divide una cadena en fragmentos utilizando uno o más espacios en blanco, guiones (-), puntos (.) o guiones bajos (_) como separadores.
                    .includes(String(tarifaKey).toUpperCase());

                return hasCompatibleInvoicingMode && (hasCompatibleTag || hasCompatibleName);
                }) : [];
            const filteredTarifas = placeholderTarifa
                ? [placeholderTarifa, ...filteredTarifasReales]
                : filteredTarifasReales;

            // Guarda les tarifes carregades i habilita el selector de tarifa comercial.
            setTarifas(filteredTarifas);
            setDisableTF(filteredTarifasReales.length === 0);
        } catch (error) {
            console.error("Error loading tarifas:", error);
            setTarifas([]);
            setDisableTF(true);
        } finally {
            setLoading(false);
        }
    }, [sector_type]);

    // Exposa les tarifes, la funcio de carrega i l'estat UI de loading/deshabilitat.
    return { tarifas, enableTarifas, loading, disableTF };
};